import { createdAtToTimestamp } from './createdAtToTimestamp'
import { enableChat } from './enableChat'
import { getChatSession } from './getChatSession'
import { getChatParamsFromUrl } from './getChatParamsFromUrl'
import { renderAgent } from './renderAgent'
import { renderChat } from './renderChat'
import { restoreMessagesFromHistory } from './restoreMessagesFromHistory'
import { saveChatSession } from './saveChatSession'
import { setChatSessionVariables } from './setChatSessionVariables'
import { sendChatBrand } from './sendChatBrand.js'

/**
 *
 * @param {import('@livechat/customer-sdk').CustomerSDK} instance
 * @param {LCChatConnectedEvent} payload
 * @param {Record<string, any>} [chatParams] - Optional chat params. If provided, uses these instead of getChatParamsFromUrl()
 * @returns
 */
export async function handleChatConnected(instance, payload, chatParams) {
  console.info('chat connected:', payload)

  const session = getChatSession()

  const r = await instance.listChats()
  const { chatsSummary } = r
  let chatData = chatsSummary.find((c) => c.id === session.chatId)

  if (!chatData && chatsSummary[0]) {
    chatData = chatsSummary[0]
    session.chatId = chatData.id
    session.chatActive = chatData.active
    session.users = chatData.users.map((u) => ({
      avatar: u.avatar || '',
      id: u.id,
      name: u.name || '',
      type: u.type,
    }))
  }

  session.users.push({
    avatar: '',
    id: payload.customer.id,
    name: payload.customer.name || '',
    type: payload.customer.type,
  })

  if (payload.greeting) {
    session.users.push({
      avatar: '',
      id: payload.greeting.agent.id,
      name: payload.greeting.agent.name || '',
      type: 'agent',
    })
  }

  if (session.chatId) {
    await restoreMessagesFromHistory(instance, session.chatId)
  }

  // If this is a new conversation and no messages exist yet,
  // show the welcome message
  if (session.conversation.length === 0) {
    const authorType = 'agent'
    const timestamp = Date.now()
    const id = `greeting-${authorType}-${timestamp}`
    /** @type {ChatSessionMessage} */
    const message = {
      id,
      authorId: 'greeting-agent',
      authorType,
      text: session.welcomeMessage,
      timestamp,
    }
    session.conversation.push(message)
    saveChatSession(session)
  }

  if (payload.greeting && /^Welcome back/i.test(payload.greeting.event.text)) {
    session.conversation.push({
      authorId: payload.greeting.event.authorId,
      authorType: 'agent',
      id: payload.greeting.event.id,
      text: payload.greeting.event.text,
      timestamp: createdAtToTimestamp(payload.greeting.event.createdAt),
    })
    saveChatSession(session)
  }

  renderChat()
  enableChat()

  // Render the agent information from greeting if available, otherwise from session
  if (payload.greeting && payload.greeting.agent) {
    renderAgent(
      payload.greeting.agent.name || 'Agent',
      payload.greeting.agent.avatar || '',
    )
  } else {
    // Fallback: use default agent information when greeting is not available
    renderAgent(
      'Ivy',
      'https://cdn.files-text.com/api/accounts/avatars/a2805e9c-4b8d-4312-8820-a5eaeb56e6f3/02d92fdf-5e7c-4c53-a4bc-0a91b5e75b1f/e484aa79-089c-42bd-bb48-47dde4efae75.jpeg',
    )
  }

  // Set chat session variables
  // Priority: 1) provided chatParams, 2) sessionStorage (for any reconnects), 3) URL params
  try {
    let urlParams = chatParams

    if (!urlParams) {
      const stored = sessionStorage.getItem('embeddedChatParams')
      if (stored) {
        try {
          urlParams = JSON.parse(stored)
        } catch (e) {
          console.warn('Failed to parse stored chat params:', e)
        }
      }
    }

    if (!urlParams) {
      urlParams = getChatParamsFromUrl()
    }

    await setChatSessionVariables(instance, urlParams)

    // Send chat brand information if uuid and chatVersion are available
    if (urlParams?.uuid && urlParams?.chatVersion) {
      await sendChatBrand(urlParams.uuid, urlParams.chatVersion)
    }
  } catch (error) {
    console.error('Failed to set chat session variables:', error)
  }
}
